import React, { useState, useEffect, useCallback } from 'react';
import {
    MessageSquareWarning, Bug, Lightbulb, Star,
    Loader2, AlertTriangle,
} from 'lucide-react';
import { adminAnalytics } from '../../services/api';
import MetricCard from './MetricCard';
import ClusterList from './ClusterList';
import ClusterDrawer from './ClusterDrawer';
import BreakdownTable from './BreakdownTable';

/**
 * FeedbackIntelligenceTab — Categorized feedback, issue/suggestion clusters.
 *
 * Props:
 *   filters: { days, district_id, campus_id }
 */

const FeedbackIntelligenceTab = ({ filters = {} }) => {
    const [themes, setThemes] = useState(null);
    const [issueClusters, setIssueClusters] = useState([]);
    const [suggestionClusters, setSuggestionClusters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedCluster, setSelectedCluster] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                days: filters.days || 30,
                ...(filters.district_id && { district_id: filters.district_id }),
                ...(filters.campus_id && { campus_id: filters.campus_id }),
            };
            const [themesRes, issuesRes, suggestionsRes] = await Promise.all([
                adminAnalytics.getFeedbackThemes(params),
                adminAnalytics.getIssueClusters(params),
                adminAnalytics.getSuggestionClusters(params),
            ]);
            setThemes(themesRes.data);
            setIssueClusters(issuesRes.data?.clusters || []);
            setSuggestionClusters(suggestionsRes.data?.clusters || []);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load feedback data.');
        } finally {
            setLoading(false);
        }
    }, [filters.days, filters.district_id, filters.campus_id]);

    useEffect(() => { loadData(); }, [loadData]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>Loading feedback intelligence…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300, flexDirection: 'column', gap: 10 }}>
                <AlertTriangle size={24} style={{ color: '#ef4444' }} />
                <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 500, margin: 0 }}>{error}</p>
            </div>
        );
    }

    const categoryCounts = themes?.by_category || [];
    const topThemes = themes?.top_themes || [];

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Feedback Intelligence</h2>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
                    Categorized feedback, issue clusters, and suggestions
                </p>
            </div>

            {/* Category breakdown cards */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 12, marginBottom: 28,
            }}>
                {categoryCounts.map(c => {
                    const icons = { bug: Bug, improvement: Lightbulb, feature_request: Star, other: MessageSquareWarning };
                    const colors = { bug: '#ef4444', improvement: '#f59e0b', feature_request: '#8b5cf6', other: '#6b7280' };
                    return (
                        <MetricCard
                            key={c.category}
                            label={c.category?.replace(/_/g, ' ') || 'Other'}
                            value={c.count ?? 0}
                            icon={icons[c.category] || MessageSquareWarning}
                            accent={colors[c.category] || '#6b7280'}
                        />
                    );
                })}
            </div>

            {/* Two-column: issues + suggestions */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 28 }}>
                <div>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Bug size={14} style={{ color: '#ef4444' }} />
                        Issue Clusters
                    </h3>
                    <ClusterList
                        clusters={issueClusters}
                        accentColor="#ef4444"
                        emptyMessage="No issue clusters found"
                        onSelect={setSelectedCluster}
                    />
                </div>

                <div>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Lightbulb size={14} style={{ color: '#f59e0b' }} />
                        Suggestion Clusters
                    </h3>
                    <ClusterList
                        clusters={suggestionClusters}
                        accentColor="#f59e0b"
                        emptyMessage="No suggestion clusters found"
                        onSelect={setSelectedCluster}
                    />
                </div>
            </div>

            {/* Top themes table */}
            {topThemes.length > 0 && (
                <div>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12 }}>
                        Top Themes
                    </h3>
                    <BreakdownTable
                        data={topThemes}
                        labelKey="theme"
                        valueKey="count"
                        labelHeader="Theme"
                        valueHeader="Mentions"
                        emptyMessage="No themes detected"
                    />
                </div>
            )}

            {/* Cluster drawer */}
            {selectedCluster && (
                <ClusterDrawer
                    cluster={selectedCluster}
                    onClose={() => setSelectedCluster(null)}
                />
            )}

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default FeedbackIntelligenceTab;
